import React from 'react';
import { Button } from '@/components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface PaginationProps {
  page: number;
  totalPages: number;
  totalElements: number;
  size: number;
  onPageChange: (page: number) => void;
  onSizeChange?: (size: number) => void;
  isLoading?: boolean;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  totalElements,
  size,
  onPageChange,
  onSizeChange,
  isLoading = false,
  pageSizeOptions = [10, 20, 50, 100],
  showPageSizeSelector = true,
}) => {
  // Don't show pagination if there's only one page and total elements fit in one page
  if (totalPages <= 1 && totalElements <= size) {
    return null;
  }

  const currentPage = page + 1; // Convert 0-indexed to 1-indexed for display
  const hasPreviousPage = page > 0;
  const hasNextPage = page < totalPages - 1;

  const handlePrevious = () => {
    if (hasPreviousPage && !isLoading) {
      onPageChange(Math.max(0, page - 1));
    }
  };

  const handleNext = () => {
    if (hasNextPage && !isLoading) {
      onPageChange(Math.min(totalPages - 1, page + 1));
    }
  };

  const startItem = totalElements === 0 ? 0 : page * size + 1;
  const endItem = Math.min((page + 1) * size, totalElements);

  return (
    <div className="flex items-center justify-between mt-4">
      <div className="flex items-center gap-4">
        <div className="text-sm text-muted-foreground">
          Showing {startItem} to {endItem} of {totalElements} results
        </div>
        {showPageSizeSelector && onSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Items per page:</span>
            <Select
              value={size.toString()}
              onValueChange={(v) => onSizeChange(parseInt(v))}
              disabled={isLoading}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((option) => (
                  <SelectItem key={option} value={option.toString()}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <div className="text-sm text-muted-foreground mr-2">
          Page {currentPage} of {totalPages || 1}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrevious}
          disabled={!hasPreviousPage || isLoading}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only md:not-sr-only md:ml-1">Previous</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleNext}
          disabled={!hasNextPage || isLoading}
          aria-label="Next page"
        >
          <span className="sr-only md:not-sr-only md:mr-1">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
